import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import StorageHelper from '@/Utility/StorageHelper';
import RADApplication from '@/Management/RADApplication';
import NavigationService from '@/Management/NavigationService';
import Loader from '@/Utility/Loader';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Messenger from '@/Utility/Messenger';
import Config from '@/Management/GlobalConfiguration';
import * as DownloadHelper from '@/Utility/DownloadHelper';
import type AssociationGridViewModelType from '@/ViewModels/Controls/AssociationGridViewModel';
import type AssociationGridType from '@/Controls/AssociationGrid';
import type ObjectDisconnectionHelperType from '@/Utility/Association/ObjectDisconnectionHelper';
import type ObjectDeletionHelperType from '@/Utility/Association/ObjectDeletionHelper';
import type LabelViewModelType from '@/ViewModels/Controls/LabelViewModel';
import type ButtonViewModelType from '@/ViewModels/Controls/ButtonViewModel';
import type DataGridViewModelType from '@/ViewModels/Controls/DataGridViewModel';
import type ToolBarViewModelType from '@/ViewModels/Controls/ToolBarViewModel';
import type LinkViewModelType from '@/ViewModels/Controls/LinkViewModel';
import type ImageViewModelType from '@/ViewModels/Controls/ImageViewModel';
import type FilterViewModelType from '@/ViewModels/Controls/FilterViewModel';
import type SearchListViewViewModelType from '@/ViewModels/Views/SearchListViewViewModel';
import type TableConfigViewModelType from '@/ViewModels/Views/TableConfigViewModel';
import type TypeSelectorWindowViewModelType from '@/ViewModels/Controls/TypeSelectorWindowViewModel';
import type TypeSelectorWindowType from '@/Controls/TypeSelectorWindow';
import Utilities from '@/Utility/Utilities';
import NavigationHelper from '@/Management/NavigationHelper';
import { IListViewDTO, IResultDTO, IChangeObjectDTO, IColumnDTO, IHideConnectedElementsRequestDTO } from "coreframetypings/dto";
import {
    DisposeBehavior,
    GridColumn,
    IDataGridViewModel,
    IDataGridViewModelConstructorParameter,
    IGridControlFactory,
    IRefreshable
} from '#/DefaultViewExtensionInterfaces';
import ListViewHelper from '@/Utility/ListViewHelper';
import UserSpecificDataHelper from '@/Utility/UserSpecificDataHelper';
import PrincipalHelper from '@/Utility/PrincipalHelper';
import FilterLinkViewModel from '@/Components/Links/FilterLink/FilterLinkViewModel';
import LocatorHelper from '@/Utility/LocatorHelper';
import PublicFilterEditLinkViewModel from '@/Components/Links/FilterLink/PublicFilterEditLinkViewModel';
import SearchListViewViewFactory from '@/Factories/ViewFactories/SearchListViewViewFactory';
import GridResizeStorageManager from '@/Utility/GridResizeStorageManager';

export default class AssociationGridFactory extends ControlFactory implements IRefreshable, IGridControlFactory {

    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelType>('LabelViewModel');
    protected LinkViewModel = ComponentManager.getViewModel<typeof LinkViewModelType>('LinkViewModel');
    protected ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelType>('ImageViewModel');

    /**
     *  @override
     */
    public viewModel: AssociationGridViewModelType;
    public dataGrid: DataGridViewModelType;
    public toolBar: ToolBarViewModelType;
    public typeSelectorOnConnect: boolean;
    public isLazy: KnockoutObservable<boolean>;
    public isDropable: boolean;

    private jQueryControl: JQuery;
    private tableConfigOpened: boolean;
    protected viewFactorySearchListView: SearchListViewViewFactory;
    private selectBaseObjectWindowViewModel: TypeSelectorWindowViewModelType;
    private staticFilter: string;
    private height: any;
    private searchViewFilter: string;
    public isModalCreating: boolean;

    public constructor(params: any) {
        super(params);

        this.jQueryControl = null;
        this.tableConfigOpened = false;
        this.viewFactorySearchListView = null;
        this.selectBaseObjectWindowViewModel = null;

        this.staticFilter =
            this.paramOrDefault(params.staticFilter,
                this.detailViewItem &&
                    this.detailViewItem.listView &&
                    this.detailViewItem.listView.filter ?
                    this.detailViewItem.listView.filter : '');

        this.searchViewFilter = this.paramOrDefault('');

        this.labelPosition = this.paramOrDefault(params.labelPosition, 'Top');
        this.colspan = this.paramOrDefault(params.colspan, 'Two');
        this.typeSelectorOnConnect = this.paramOrDefault(params.typeSelectorOnConnect, false);
        this.height = this.paramOrDefault(params.height, null);
        this.isLazy = this.observableOrDefault(params.lazyLoading, true);
        this.isDropable = this.paramOrDefault(params.isDropable, false);
        this.isModalCreating = this.paramOrDefault(params.isModalCreating, false);

        this.registerForMessageId('RefreshAssociationsMessage', () => {
            if (!this.isDisposed)
                this.refresh();
        });
    }

    /**
     *  @override
     */
    control: AssociationGridType;

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.dataGrid = this.createDataGridViewModel();
        
        this.toolBar = this.createToolBarViewModel();
        this.viewModel = this.createViewModel();

        this.createFilterSubscriptions();

        this.control = this.createControl();
        const tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.registerObservableValidation(this.viewModel.dataGrid.data);

        return this.jQueryControl;
    }
    private createFilterSubscriptions() {
        // refresh the grid when the sort-state of any sortElement-VM changes
        if (this.dataGrid.filter != null) {
            this.viewModel.defineSubscription(this.dataGrid.filter.orderBy,
                filterVM_orderBy => {
                    if (this.dataGrid.filter.suspendOrderByUpdateToGrid !== true) {
                        this.dataGrid.onSortChanged(filterVM_orderBy);
                    }
                });

            this.viewModel.defineSubscription(this.dataGrid.filter.serializedFilter, (sf) => {
                NavigationService.getInstance().setHiddenParameterToAddress(this.viewModel.identifier, 'SerializedFilter-' + this.detailViewItem.name, sf);
            });
        }


        // forward changes from grid-ordering to filter-VM-ordering
        this.viewModel.defineSubscription((this.dataGrid as IDataGridViewModel).orderBy, orderBy => {
            this.dataGrid.filter.suspendOrderByUpdateToGrid = true;
            this.dataGrid.filter.orderBy(orderBy);
            this.dataGrid.filter.suspendOrderByUpdateToGrid = false;
        });
    }

    protected createViewModel(): AssociationGridViewModelType {
        const AssociationGridViewModel = ComponentManager.getViewModel<typeof AssociationGridViewModelType>('AssociationGridViewModel');
        return new AssociationGridViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            invalidationText: this.invalidationText,
            isValid: this.isValid,
            isSummaryViewMode: this.isSummaryViewMode,
            dataGrid: this.dataGrid,
            toolBar: this.toolBar,
            isDropable: this.isDropable,
            contextPdoId: this.context.genericObject.pdoId,
            contextTypeName: this.context.genericObject.pdTypeName,
            associationTypeName: this.detailViewItem.listView ? this.detailViewItem.listView.typeName : null,
            associationName: this.detailViewItem.listView ? this.detailViewItem.listView.relationName : null,
            onAssociationChanged: this.onAssociationChanged.bind(this)
        });
    }

    protected createControl(): AssociationGridType {
        const AssociationGrid = ComponentManager.getControl<typeof AssociationGridType>('AssociationGrid');
        return new AssociationGrid(this.viewModel);
    }

    public createDataGridViewModel(): DataGridViewModelType {
        const DataGridViewModel = ComponentManager.getViewModel<typeof DataGridViewModelType>('DataGridViewModel');
        const parameters = this.createDataGridViewModelParameter();
        return new DataGridViewModel(parameters);
    }

    public createDataGridViewModelParameter(): IDataGridViewModelConstructorParameter {
        const FilterViewModel = ComponentManager.getViewModel<typeof FilterViewModelType>('FilterViewModel');

        const dvName = this.context?.detailView()?.name;
        const dviName = this.detailViewItem?.name;
        
        const storageColumnDefinitionKey = this.detailViewItem.listView.name + '_ColumnDefinition';
        let pageSize = UserSpecificDataHelper.loadUserSpecificData(this.detailViewItem.listView.name + '_PageSize', RADApplication.getInstance().configuration.possibleAssoziationListViewPages[0]);
        this.addComputed(pageSize);

        const columnPreset = StorageHelper.bind(this.detailViewItem.listView.name + '_ColumnPreset', {});
        const orderBy = UserSpecificDataHelper.loadUserSpecificData(this.detailViewItem.listView.name + '_Order', null);
        this.addComputed(columnPreset);
        this.addComputed(orderBy);

        let columnDef = this.loadColumnDefinitions(storageColumnDefinitionKey);
        if (this.detailViewItem.listView.showMoveButtons) { // showMoveButtons == true heißt, dass dies ein RELN-Grid ist
            const originalColumnDef = columnDef;
            columnDef = this.defineComputed(() => {
                const originalColumns = originalColumnDef();
                return originalColumns.map(c => ({
                    ...c,
                    // RELN Grids können nur manuell über die Move-Buttons sortiert werden,
                    // deshalb wird die Sortierung über die Spalte deaktiviert
                    sortable: false
                }));
            });
        }

        return {
            height: this.height,
            isAssociationDataGrid: true,
            isDisplayed: this.isDisplayed,
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.datagrid',
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            filter: new FilterViewModel({
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.datagrid.filter',
                isDisplayed: this.isDisplayed,
                serializedFilter: this.context.serializedFilters?.[this.detailViewItem.name],
                onSubmit: () => {
                    this.refresh()
                },
                includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
                includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
                includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
                listViewPdoId: this.detailViewItem.listView.listViewID,
                pdTypeName: this.detailViewItem.listView.typeName,
                shouldShowChips: this.detailViewItem.listView.showFilter,
                shouldShowSaveInputs: true,
                shouldShowSorting: true,
                publicUserData: this.context.publicUserData['LV: ' + this.detailViewItem.listView.typeName]
            }),
            staticFilter: this.staticFilter,
            defaultIterator: this.context.defaultIterators[this.detailViewItem.name],
            pdTypeName: this.detailViewItem.listView.typeName,
            columnDefinition: columnDef as any, // TODO: Richtigen Typ verwenden
            storageColumnDefinitionKey: storageColumnDefinitionKey,
            columnPreset: columnPreset,
            isSummaryViewMode: this.isSummaryViewMode,
            pageSizes: RADApplication.getInstance().configuration.possibleAssoziationListViewPages,
            pageSize: pageSize,
            page: 1,
            orderBy: orderBy,
            defaultOrder: this.detailViewItem.listView.defaultOrder,
            ignoreOrder: this.detailViewItem.listView.showMoveButtons, // Wenn manuell sortiert wird, darf keine anderweitige Sortierung greifen
            rowLimit: this.detailViewItem.listView.rowLimit,
            isEnabled: this.isEnabled,
            associationPdoId: this.context.genericObject.pdoId,
            associationType: this.context.genericObject.pdTypeName,
            associationName: this.detailViewItem.listView.assoziationListView ? this.detailViewItem.listView.relationName : null,
            onDoubleClick: this.onDoubleClick.bind(this),
            identity: 'PDO_ID',
            isPrincipalIndependent: this.detailViewItem.listView.isPrincipalIndependent,
            includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
            includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
            includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
            isLazy: this.isLazy,
            isMultiColumnSort: true,
            renderGridAsync: true,
            verticalResize: {
                isUserResizeEnabled: true,
                storageKeyPrefix: dvName && dviName ? `${dvName}#${dviName}` : null
            },
        };
    }

    protected loadColumnDefinitions(storageColumnDefinitionKey: string): KnockoutComputed<IColumnDTO[]> {
        return ListViewHelper.loadColumnDefinitions(storageColumnDefinitionKey, this.detailViewItem.listView.columns, this.getExtendedColumnNames());
    }

    protected getDefaultColumns(): IColumnDTO[] {
        return this.detailViewItem.listView.columns.concat(this.dataGrid.getExtendedColumns());
    }

    protected getExtendedColumnNames(): string[] {
        return [];
    }

    protected onDoubleClick(pdoId: string, rowData: any, openInNewWindow: boolean) {
        this.clickEditHandler(pdoId, openInNewWindow);
    }

    public clickEditHandler(pdoId: string, openInNewWindow: boolean): void {
        if (!this.showEditButton() || pdoId === this.detailViewItem.disableNavigation || _.contains(this.viewModel.notNavigateableAssociations, pdoId))
            return;

        if (openInNewWindow) {
            NavigationHelper.navigateAssociation(this.detailViewItem.listView.typeName,
                pdoId,
                this.createAssociationNavigationParameter(),
                openInNewWindow);
        } else {
            this.context.createContext(this, this.detailViewItem.listView, pdoId, undefined, this.getPrincipaFilterMode(), null, false, 'EDIT');
        }
    }

    /**
     * Werden benötigt, falss die Assoziation in einem neuen Fenster geöffnet werden soll
     */
    protected createAssociationNavigationParameter(): any {
        return {
            contextSource: this.context.isSummaryViewMode() ? 'SV' : 'DV',
            contextTypeName: this.context.getPdTypeName(),
            contextObject: this.context.genericObject.pdoId,
            contextRelation: this.detailViewItem.name,
            principalFilterMode: this.getPrincipaFilterMode()
        };
    }

    private getPrincipaFilterMode(): string {
        return PrincipalHelper.getClientFilterModeFromParameters({
            includeBasePrincipals: this.detailViewItem.listView.includeBasePrincipals,
            includeSubPrincipals: this.detailViewItem.listView.includeSubPrincipals,
            includeAllPrincipals: this.detailViewItem.listView.includeAllPrincipals,
            onlySubPrincipals: false
        });
    }

    public createToolBarViewModel(): ToolBarViewModelType {
        const ToolBarViewModel = ComponentManager.getViewModel<typeof ToolBarViewModelType>('ToolBarViewModel');
        return new ToolBarViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar',
            detectOverflowOnSecondaryItems: false,
            primary: this.createToolbarPrimaryLinks(),
            secondary: this.createToolbarSecondaryLinks()
        });
    }

    public createToolbarPrimaryLinks(): KnockoutComputed<LinkViewModelType[]> {
        return this.defineComputed(() => {
            let linkViewModels = [];

            linkViewModels.push(this.createCreateNewAndConnectLinkViewModel());
            linkViewModels.push(this.createFindAndConnectLinkViewModel());
            linkViewModels.push(this.createEditConnectedLinkViewModel());

            linkViewModels.push(this.createDisconnectLinkViewModel());
            linkViewModels.push(this.createDisconnectAndDeleteLinkViewModel());

            if (_.find(linkViewModels, (link) => {
                return link.isVisible();
            })) {
                linkViewModels.push({});
            }

            linkViewModels.push(this.createMoveUpLinkViewModel());
            linkViewModels.push(this.createMoveDownLinkViewModel());

            if (this.detailViewItem.listView.showMoveButtons) {
                linkViewModels.push({});
            }

            return linkViewModels;
        });
    }

    public createCreateNewAndConnectLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.create',
            label: this.createPrimaryToolbarLabel('CREATE_NEW_AND_CONNECT', 'toolbar.create.label'),
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_CREATE,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.create.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'CREATE_NEW_AND_CONNECT_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'CREATE_NEW_AND_CONNECT_TOOLTIP'
            }),
            isActive: false,
            isVisible: this.defineComputed(() => {
                return this.isEnabled() && this.detailViewItem.listView.showButtonCreate;
            }),
            isEnabled: this.defineComputed(() => {
                return this.isEnabled() && !this.isHistoricalIterator(this.dataGrid.iterator());
            }),
            isFocusable: this.isFocusable,
            onClick: () => {
                $.when(this.context.notifyBeforeUpdatedAssociation(this.detailViewItem.listView.relationName, 'connect', { toPdoIds: [null] }))
                    .then((doUpdateAssociation) => {
                        if (doUpdateAssociation == null || doUpdateAssociation === true) {
                            this.context.createContext(this, this.detailViewItem.listView, null, null, null, null, this.isModalCreating, 'CREATE');
                        }
                    });
            }
        });
    }

    private isHistoricalIterator(iterator: any): boolean {
        return iterator != null && iterator.returnHistoricalData === true;
    }

    public createFindAndConnectLinkViewModel(): LinkViewModelType {
        const typeSelectorWindowViewModel = ComponentManager.getViewModel<typeof TypeSelectorWindowViewModelType>('TypeSelectorWindowViewModel');
        const linkViewModel = new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.select',
            label: this.createPrimaryToolbarLabel('FIND_AND_CONNECT', 'toolbar.select.label'),
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_SELECT,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.select.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'FIND_AND_CONNECT_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'FIND_AND_CONNECT_TOOLTIP'
            }),
            isActive: false,
            isVisible: this.defineComputed(() => {
                return this.isEnabled() && this.detailViewItem.listView.showButtonSelect;
            }),
            isEnabled: this.isEnabled,
            isFocusable: this.isFocusable,
            onClick: () => {
                const TypeSelectorWindow = ComponentManager.getControl<typeof TypeSelectorWindowType>('TypeSelectorWindow');

                if (this.typeSelectorOnConnect && this.detailViewItem.listView.types.length > 1) {
                    if (this.selectBaseObjectWindowViewModel == null) {
                        this.selectBaseObjectWindowViewModel = typeSelectorWindowViewModel.createSubTypeSelectorWindow(
                            this.detailViewItem.listView.ergName,
                            this.detailViewItem.listView.types,
                            (typeName: string) => {
                                RADEnvironmentProxy.getInstance().getListViewByName({
                                    listViewName: typeName + '_LV'
                                }).then((listView: IListViewDTO) => {
                                    listView = this.copyListViewProperties(this.detailViewItem.listView, listView);
                                    this.openSearchListView(listView, linkViewModel);
                                });
                            },
                            this.defineComputed(() => {
                                if (Utilities.isNullOrUndefined(this.locator()))
                                    return null;
                                return `${this.locator()}.${this.detailViewItem.name}`;
                            }),
                            () => {
                                //NavigationService.getInstance().cleanRegion('Modal'); 
                                //Workaround: cleanRegion entfernt alle Windows, d.h. manuelles entfernen
                                if (this.selectBaseObjectWindowViewModel && this.selectBaseObjectWindowViewModel.window) {
                                    const $context = this.selectBaseObjectWindowViewModel.window.content();
                                    if ($context) {
                                        $context.closest('.k-window').remove();
                                    }
                                }
                                this.selectBaseObjectWindowViewModel = null;
                            }
                        );

                        const selectBaseObjectWindow = new TypeSelectorWindow(this.selectBaseObjectWindowViewModel);
                        selectBaseObjectWindow.getTuple();
                    }
                }
                else {
                    this.openSearchListView(this.detailViewItem.listView, linkViewModel);

                }
            }
        });
        return linkViewModel;
    }

    public createEditConnectedLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.edit',
            label: this.createPrimaryToolbarLabel('EDIT_CONNECTED', 'toolbar.edit.label'),
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_EDIT,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.edit.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'EDIT_CONNECTED_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'EDIT_CONNECTED_TOOLTIP'
            }),
            isActive: false,
            isVisible: this.showEditButton(),
            isEnabled: this.createEditEnabledComputed(),
            isFocusable: this.isFocusable,
            onClick: (openInNewWindow) => {
                const selected = this.dataGrid.selected();
                const pdoId = selected[0];
                this.clickEditHandler(pdoId, openInNewWindow);
            }
        });
    }

    protected showEditButton(): boolean {
        return this.detailViewItem.listView.showButtonEdit;
    }

    public createDisconnectLinkViewModel() {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.disconnect',
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_DISCONNECT,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.disconnect.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'DISCONNECT_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'DISCONNECT_TOOLTIP'
            }),
            isActive: false,
            isVisible: this.defineComputed(() => {
                return this.isEnabled() && this.detailViewItem.listView.showButtonDisconnect;
            }),
            isFocusable: this.isFocusable,
            isEnabled: this.createDisconnectEnabledComputed(),
            onClick: () => {
                var toPdoIds = this.dataGrid.selected();

                this.disconnect(toPdoIds);

            }
        });
    }

    public disconnect(toPdoIds: Array<string>): void {
        const pdoId = this.context.genericObject.pdoId;
        const associationName = this.detailViewItem.listView.relationName;
        const topdTypeName = this.detailViewItem.listView.typeName;
        const toPdoiDs = toPdoIds;
        const nestedContextName = this.context.getNestedContextPath();

        const ObjectDisconnectionHelper = ComponentManager.getUtility<typeof ObjectDisconnectionHelperType>('ObjectDisconnectionHelper');
        const odh = new ObjectDisconnectionHelper({
            contextPdoId: pdoId,
            associationName: associationName,
            pdTypeName: topdTypeName,
            affectedObjectIds: toPdoiDs,
            disconnectionCallback: () => {
                this.disconnectItems(pdoId, associationName, topdTypeName, toPdoiDs, nestedContextName);
            },
            confirmDeletionCallback: (value) => {
                if (value === true) {
                    this.deleteItems(associationName, topdTypeName, toPdoiDs);
                }
            }
        });

        odh.ExecuteCheck();
    }

    public createDisconnectAndDeleteLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.delete',
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_DELETE,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.delete.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'DISCONNECT_AND_DELETE_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'DISCONNECT_AND_DELETE_TOOLTIP'
            }),
            isActive: false,
            isVisible: this.defineComputed(() => {
                return this.isEnabled() && this.detailViewItem.listView.showButtonDelete;
            }),
            isFocusable: this.isFocusable,
            isEnabled: this.createDisconnectAndDeleteEnabledComputed(),
            onClick: () => {
                const pdTypeName = this.detailViewItem.listView.typeName;
                const pdoIds = this.dataGrid.selected();
                const associationName = this.detailViewItem.listView.relationName;
                const ObjectDeletionHelper = ComponentManager.getUtility<typeof ObjectDeletionHelperType>('ObjectDeletionHelper');

                const odh = new ObjectDeletionHelper({
                    pdTypeName: pdTypeName,
                    affectedObjectIds: pdoIds,
                    confirmDeletionCallback: (value) => {
                        if (value === true) {
                            this.deleteItems(associationName, pdTypeName, pdoIds);
                        }
                    }
                });

                odh.ExecuteCheck();

            }
        });
    }

    protected createEditEnabledComputed(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            const selected = this.dataGrid.selected();
            if (selected.length !== 1) return false;
            const pdoId = selected[0];
            if (pdoId === this.detailViewItem.disableNavigation ||
                _.contains(this.viewModel.notNavigateableAssociations, pdoId)) return false;
            return true;
        });
    }

    protected createDisconnectEnabledComputed(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            const selected = this.dataGrid.selected();
            return this.isEnabled() &&
                selected.length >= 1 &&
                !this.isHistoricalIterator(this.dataGrid.iterator());
        });
    }

    protected createDisconnectAndDeleteEnabledComputed(): KnockoutComputed<boolean> {
        return this.defineComputed(() => {
            const selected = this.dataGrid.selected();
            return this.isEnabled() && selected.length >= 1;
        });
    }

    public createMoveUpLinkViewModel(): LinkViewModelType {
        const moveUpEnabled = this.observableOrDefault(true);

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.up',
            image: new this.ImageViewModel({
                source: Config.inlineImages.MOVE_UP,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.up.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_UP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MOVE_UP'
            }),
            isActive: false,
            isVisible: this.detailViewItem.listView.showMoveButtons,
            isEnabled: this.defineComputed(() => {
                const data = this.dataGrid.data();
                const selected = this.dataGrid.selected();
                const page = this.dataGrid.page();

                // Wenn ein Filter aktiv ist, funktioniert der Index-basierte Swap nicht.
                // Deshalb wird bei aktiviertem Filter der Button deaktiviert.
                if (this.isFilterActive())
                    return false;

                if (!this.isEnabled() || selected.length !== 1)
                    return false;

                const index = _.findIndex(data, (e: any) => { return e.PDO_ID === selected[0]; });

                if (index === -1 || index === 0 && page === 1)
                    return false;

                return moveUpEnabled();
            }),
            isFocusable: this.isFocusable,
            onClick: () => {
                if (moveUpEnabled()) {
                    moveUpEnabled(false);

                    const data = this.dataGrid.data();
                    const selected = this.dataGrid.selected();
                    const page = this.dataGrid.page();
                    const pageSize = this.dataGrid.pageSize();
                    let newPage = page;

                    if (selected.length !== 1)
                        return;

                    for (let j = 0; j < data.length; j++) {
                        if (selected[0] === data[j].PDO_ID) {
                            const first = (page - 1) * pageSize + j;
                            const second = first - 1;

                            if (first > 0) {
                                RADEnvironmentProxy.getInstance().swap({
                                    pdoID: this.context.genericObject.pdoId,
                                    assoziationName: this.dataGrid.associationName,
                                    first: first,
                                    second: second
                                }).then((resultDto: IResultDTO) => {
                                    if (this.validateStatus(resultDto)) {
                                        this.onAssociationChanged();

                                        if (j === 0) {
                                            newPage--;
                                        }

                                        this.dataGrid.page(newPage);
                                        this.refresh().done(() => {
                                            this.async(() => {
                                                moveUpEnabled(true);
                                            }, 100);
                                        });
                                    }
                                });
                            }

                            break;
                        }
                    }
                }
            }
        });
    }

    public createMoveDownLinkViewModel(): LinkViewModelType {
        const moveDownEnabled = this.observableOrDefault(true);

        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.down',
            image: new this.ImageViewModel({
                source: Config.inlineImages.MOVE_DOWN,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.down.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'MOVE_DOWN'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'MOVE_DOWN'
            }),
            isActive: false,
            isVisible: this.detailViewItem.listView.showMoveButtons,
            isEnabled: this.defineComputed(() => {
                const data = this.dataGrid.data();
                const selected = this.dataGrid.selected();
                const page = this.dataGrid.page();
                const pageSize = this.dataGrid.pageSize();
                const totalRows = this.dataGrid.totalRows();

                // Wenn ein Filter aktiv ist, funktioniert der Index-basierte Swap nicht.
                // Deshalb wird bei aktiviertem Filter der Button deaktiviert.
                if (this.isFilterActive())
                    return false;

                if (!this.isEnabled() || selected.length !== 1)
                    return false;

                const index = _.findIndex(data, (e: any) => { return e.PDO_ID === selected[0]; });

                if (index === -1 || (pageSize * (page - 1)) + index >= totalRows - 1)
                    return false;

                return moveDownEnabled();
            }),
            isFocusable: this.isFocusable,
            onClick: () => {
                if (moveDownEnabled()) {
                    moveDownEnabled(false);

                    const data = this.dataGrid.data();
                    const selected = this.dataGrid.selected();
                    const page = this.dataGrid.page();
                    const pageSize = this.dataGrid.pageSize();
                    let newPage = page;

                    if (selected.length != 1)
                        return;

                    for (let j = data.length - 1; j >= 0; j--) {
                        if (selected[0] === data[j].PDO_ID) {
                            const first = (page - 1) * pageSize + j;
                            const second = first + 1;

                            if (second < this.dataGrid.totalRows()) {
                                RADEnvironmentProxy.getInstance().swap({
                                    pdoID: this.context.genericObject.pdoId,
                                    assoziationName: this.dataGrid.associationName,
                                    first: first,
                                    second: second
                                }).then((resultDto: IResultDTO) => {
                                    if (this.validateStatus(resultDto)) {
                                        this.onAssociationChanged();

                                        if (second >= data.length) {
                                            newPage++;
                                        }

                                        this.dataGrid.page(newPage);
                                        this.refresh().done(() => {
                                            this.async(() => {
                                                moveDownEnabled(true);
                                            }, 100);
                                        });
                                    }
                                });
                            }

                            break;
                        }
                    }
                }
            }
        });
    }

    private isFilterActive(): boolean {
        const filterExpression = this.dataGrid.getFilterExpression();
        return filterExpression.components?.length > 0;
    }

    public createToolbarSecondaryLinks(): KnockoutComputed<LinkViewModelType[]> {
        return this.defineComputed(() => {
            let linkViewModels = [];

            const isNotSummaryViewMode = this.isSummaryViewMode === false;
            const isShowToolbarSecondaryItemsInSv = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowToolbarSecondaryItemsInSV');
            const isShowTableconfigButtonInDv = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowTableconfigButtonInDV');

            if (isNotSummaryViewMode || isShowToolbarSecondaryItemsInSv) {
                linkViewModels.push(this.createRefreshLinkViewModel());

                ko.ignoreDependencies(() => {
                    const link = new FilterLinkViewModel({
                        label: new this.LabelViewModel({ // Default-Label (Ergname) überschreiben. Wir wollen hier kein Label.
                            ergName: '',
                            isVisible: false,
                        }),
                        dataSource: this.dataGrid,
                        listView: this.detailViewItem.listView,
                        parentLocator: LocatorHelper.buildLocator(this.locator, this.detailViewItem.name, 'toolbar')
                    });

                    linkViewModels.push(link);

                    let editLink = new PublicFilterEditLinkViewModel({
                        label: new this.LabelViewModel({ // Default-Label (Ergname) überschreiben. Wir wollen hier kein Label.
                            ergName: '',
                            isVisible: false,
                        }),
                        filterSource: this.dataGrid.filter,
                        listView: this.detailViewItem.listView,
                        parentLocator: LocatorHelper.buildLocator(this.locator, this.detailViewItem.name, 'toolbar'),
                        filterLink: link
                    });

                    if (this.dataGrid.filter?.isVisible()) {
                        if (this.dataGrid.filter.editPublicChips()) {
                            editLink.showPublicFilterEdit();
                        }
                        else {
                            link.showFilter();
                        }
                    }

                    linkViewModels.push(editLink);
                });

                if (isShowTableconfigButtonInDv) {
                    linkViewModels.push(this.createTableConfigLinkViewModel());
                }


                if (this.areExportElementsVisible()) {
                    linkViewModels.push({});
                }
                
                linkViewModels.push(this.createXLSExportLinkViewModel());
                linkViewModels.push(this.createCSVExportLinkViewModel());
            }

            return linkViewModels;
        });
    }

    public createRefreshLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.refresh',
            image: new this.ImageViewModel({
                source: Config.inlineImages.REFRESH,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.refresh.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'REFRESH_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'REFRESH_TOOLTIP'
            }),
            isActive: false,
            isVisible: true,
            isEnabled: true,
            isFocusable: this.isFocusable,
            onClick: () => {
                this.refresh();
            }
        });
    }

    public createFindLinkViewModel(): LinkViewModelType {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.find',
            image: new this.ImageViewModel({
                source: Config.inlineImages.FILTER,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.find.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'FILTER_TOOLTIP'
                })
            }),
            isActive: this.definePureComputed(() => {
                const filters = this.dataGrid.filter.elements();
                return filters.length > 0;
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'FILTER_TOOLTIP'
            }),
            isVisible: this.detailViewItem.listView.showFilter,
            isEnabled: true,
            isFocusable: this.isFocusable,
            onClick: () => {
                const elements = this.dataGrid.filter.elements();

                if (elements.length > 0) {
                    this.dataGrid.filter.elements([]);
                } else {
                    this.dataGrid.filter.addNewElement();
                }
            }
        });
    }

    public createTableConfigLinkViewModel(): LinkViewModelType {
        const TableConfigViewModel = ComponentManager.getViewModel<typeof TableConfigViewModelType>('TableConfigViewModel');
        const storageColumnDefinitionKey = this.detailViewItem.listView.name + '_ColumnDefinition';
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.toolbar.tableconfig',
            image: new this.ImageViewModel({
                source: Config.inlineImages.TOOLBAR_TABLECONFIG,
                parentLocator: this.locator,
                locator: this.detailViewItem.name + '.toolbar.tableconfig.image',
                alternativeText: new this.LabelViewModel({
                    ergName: 'CUSTOM_COLUMNS_TOOLTIP'
                })
            }),
            tooltip: new this.LabelViewModel({
                ergName: 'CUSTOM_COLUMNS_TOOLTIP'
            }),
            isActive: false,
            isVisible: this.detailViewItem.listView.showButtonCustomColumns,
            isEnabled: true,
            isFocusable: this.isFocusable,
            onClick: () => {
                if (this.tableConfigOpened)
                    return;

                const columnDef = this.loadColumnDefinitions(storageColumnDefinitionKey);
                this.tableConfigOpened = true;
                const tableConfig = NavigationService.getInstance().loadViewFactoryIsolated('TableConfig', {
                    viewModel: new TableConfigViewModel({
                        userSpecificDataKey: storageColumnDefinitionKey,
                        parentLocator: this.locator,
                        locator: this.detailViewItem.name + 'tableconfig',
                        columnDefinition: columnDef,
                        defaultDefinition: this.getDefaultColumns(),
                        onClose: () => {
                            this.tableConfigOpened = false;
                            NavigationService.getInstance().removeViewFactoryIsolated(tableConfig);
                        }
                    })
                }, true);
            }
        });
    }

    public createXLSExportLinkViewModel(): LinkViewModelType {
        const exportType = 'XLS';
        const visible = this.detailViewItem.listView.showButtonXLSExport;
        const labelParams = { isVisible: false };
        const imageParams = { isVisible: true, source: Config.inlineImages.XLS_DOCUMENT };
        const typeName = this.detailViewItem.listView.typeName;

        return this.buildExportLinkViewModel(exportType, visible, labelParams, imageParams, typeName);
    }

    public createCSVExportLinkViewModel(): LinkViewModelType {
        const exportType = 'CSV';
        const visible = this.detailViewItem.listView.showButtonCSVExport;
        const labelParams = { isVisible: false };
        const imageParams = { isVisible: true, source: Config.inlineImages.CSV_DOCUMENT };
        const typeName = this.detailViewItem.listView.typeName;

        return this.buildExportLinkViewModel(exportType, visible, labelParams, imageParams, typeName);
    }

    public disconnectItems(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): void {
        $.when(this.context.notifyBeforeUpdatedAssociation(associationName, 'disconnect', { toPdoIds: toPdoIds }))
            .then((doUpdateAssociationSync) => {
                if (doUpdateAssociationSync == null || doUpdateAssociationSync === true) {
                    Loader.getInstance().show();
                    RADEnvironmentProxy.getInstance().disconnectMany({
                        pdoID: pdoId,
                        assoziationName: associationName,
                        topdTypeName: topdTypeName,
                        toPDOIDs: toPdoIds,
                        nestedContextName: nestedContextName
                    }).then((changeObject: IChangeObjectDTO) => {
                        // Auch im Fehlerfall kann sich die Assoziation geändert haben, da alle Objekte bis zum Fehler getrennt werden
                        // Nachfolgende Elemente (ab Index, wo der Fehler auftritt) werden nicht mehr getrennt
                        this.validateStatus(changeObject);
                        this.refresh();
                        this.onAssociationChanged();
                    }).always(() => {
                        Loader.getInstance().hide();
                    });
                }
            });
    }

    public deleteItems(associationName: string, pdTypeName: string, pdoIds: Array<string>): JQueryPromise<any> {
        return $.when(this.context.notifyBeforeUpdatedAssociation(associationName, 'disconnectAndDelete', { toPdoIds: pdoIds }))
            .then((doUpdateAssociation) => {
                if (doUpdateAssociation == null || doUpdateAssociation === true) {
                    Loader.getInstance().show();

                    return RADEnvironmentProxy.getInstance().deleteManyObjects({
                        pdTypeName: pdTypeName,
                        pdoIDs: pdoIds
                    }).then((changeObject: IChangeObjectDTO) => {
                        this.dataGrid.selected([]);
                        if (this.validateStatus(changeObject)) {
                            this.refresh();
                            this.onAssociationChanged();
                        }
                    }).always(() => {
                        Loader.getInstance().hide();
                    });
                }

                return null;
            });
    }

    public createPrimaryToolbarLabel(ergNameKey: string, locator: string): LabelViewModelType {
        return new this.LabelViewModel({
            ergName: ergNameKey,
            isVisible: false,
            parentLocator: this.locator,
            locator: this.detailViewItem.name + '.' + locator
        });
    }

    public buildExportLinkViewModel(exportType: string, visible: boolean, labelParams: Object, imageParams: Object, typeName: string): LinkViewModelType {
        return new this.LinkViewModel({
            parentLocator: this.locator,
            locator: typeName + '.toolbar.export_' + exportType,
            label: new this.LabelViewModel($.extend({
                ergName: exportType + '_EXPORT_TOOLTIP',
                parentLocator: this.locator,
                locator: typeName + '.toolbar.export_' + exportType + '.label'
            }, labelParams)),

            image: new this.ImageViewModel($.extend(imageParams, {
                parentLocator: this.locator,
                locator: typeName + '.toolbar.export_' + exportType + '.image',
                alternativeText: new this.LabelViewModel({
                    ergName: exportType + '_EXPORT_TOOLTIP'
                })
            })),

            tooltip: new this.LabelViewModel({
                ergName: exportType + '_EXPORT_TOOLTIP'
            }),

            isVisible: visible,
            isActive: false,
            isEnabled: true,
            onClick: () => {
                const iteratorId = this.dataGrid.getIteratorId();

                if (iteratorId != null) {
                    Loader.getInstance().show();

                    RADEnvironmentProxy.getInstance().exportData({
                        iteratorID: iteratorId,
                        exportType: exportType,
                        columns: _.pluck(_.filter(this.dataGrid.columns(), (column: GridColumn) => { return !column.hidden; }), 'title'),
                        filter: null
                    }).then((downloadUrl: string) => {
                        DownloadHelper.openDownloadDialogFromApplicationAsync(downloadUrl);
                    }).always(() => {
                        Loader.getInstance().hide();
                    });
                }
            }
        });
    }

    public notifyDetailViewClosed(topdTypeName: string, toPDOID: string): void {
        if (this.isDisposed)
            return;
        this.refresh();
        this.context.notifyUpdatedAssociation(this.detailViewItem.listView.relationName);
    }

    public copyListViewProperties(source: IListViewDTO, target: IListViewDTO): IListViewDTO {
        target.relationName = source.relationName;

        return target;
    }

    private createHideConnectedElementsRequest(): IHideConnectedElementsRequestDTO {
        if (!this.detailViewItem.listView.hideConnectedElements)
            return null;

        return {
            hide: true,
            associationName: this.viewModel.associationName
        };
    }

    public openSearchListView(listView: IListViewDTO, buttonToRefocus: ButtonViewModelType | LinkViewModelType): void {
        const SearchListViewViewModel = ComponentManager.getViewModel<typeof SearchListViewViewModelType>('SearchListViewViewModel');
        if (this.viewFactorySearchListView == null) {
            this.viewFactorySearchListView = NavigationService.getInstance().loadViewFactoryIsolated<SearchListViewViewFactory>('SearchListView', {
                viewModel: new SearchListViewViewModel({
                    parentLocator: this.locator,
                    locator: this.detailViewItem.name + '.search-view',
                    contextPdoId: this.context.genericObject.pdoId,
                    listView: listView,
                    columnsToRemove: this.getExtendedColumnNames(),
                    allowMultiSelect: true,
                    serializedFilter: this.searchViewFilter,
                    hideConnectedElementsRequest: this.createHideConnectedElementsRequest(),
                    callback: (toPdoIds: Array<string>) => {
                        $.when(this.context.notifyBeforeUpdatedAssociation(listView.relationName, 'connect', { toPdoIds: toPdoIds }))
                            .then((doUpdateAssociation) => {
                                if (doUpdateAssociation == null || doUpdateAssociation === true) {
                                    Loader.getInstance().show();
                                    RADEnvironmentProxy.getInstance().connectMany({
                                        pdoID: this.context.genericObject.pdoId,
                                        assoziationName: listView.relationName,
                                        topdTypeName: listView.typeName,
                                        toPDOIDs: toPdoIds,
                                        nestedContextName: this.context.getNestedContextPath()
                                    }).then((changeObject: IChangeObjectDTO) => {
                                        // Auch im Fehlerfall kann sich die Assoziation geändert haben, da alle Objekte bis zum Fehler verbunden werden
                                        // Nachfolgende Elemente (ab Index, wo der Fehler auftritt) werden nicht mehr verbunden
                                        this.validateStatus(changeObject);
                                        this.refresh();
                                        this.onAssociationChanged();
                                    }).always(() => {
                                        Loader.getInstance().hide();
                                    });
                                }
                            });

                    },
                    onClose: () => {
                        this.viewFactorySearchListView.viewModel.dataGrid.filter.forceFilterExpressionObjectCalculation();
                        this.searchViewFilter = this.viewFactorySearchListView.viewModel.dataGrid.filter.serializedFilter();
                        buttonToRefocus.hasFocus(true);
                        NavigationService.getInstance().removeViewFactoryIsolated(this.viewFactorySearchListView);
                        this.viewFactorySearchListView = null;
                    }
                })
            }, true);
        }
    }

    private areExportElementsVisible(): boolean {
        return this.detailViewItem.listView.showButtonXLSExport || this.detailViewItem.listView.showButtonCSVExport || this.detailViewItem.listView.showButtonPDFExport
    }

    /**
    * Überschreibt das Standard-Disposing
    */
    protected disposeInternal(): void {
        Messenger.unregisterForMessageId('RefreshAssociationsMessage', null, this);

        if (this.selectBaseObjectWindowViewModel && !this.selectBaseObjectWindowViewModel.isDisposed) {
            this.selectBaseObjectWindowViewModel.dispose();
        }
        if (this.viewFactorySearchListView != null && !this.viewFactorySearchListView.isDisposed) {
            NavigationService.getInstance().removeViewFactory(this.viewFactorySearchListView);
        }
        super.disposeInternal();
    }

    private onAssociationChanged(): void {
        this.context.genericObject.isModified(true);
        this.context.notifyUpdatedAssociation(this.detailViewItem.listView.relationName);
        this.context.notifyFactoryRefreshed(this.detailViewItem);
    }

    public getContainerPostfix(): KnockoutObservable<string> {
        return this.defineComputed(() => {
            if (this.isDisposed || Utilities.isNullOrUndefined(this.dataGrid))
                return '';

            if (!Utilities.isNullOrUndefinedOrBlank(this.dataGrid.fullExtentCount()))
                return this.dataGrid.fullExtentCount();

            if (Utilities.isNullOrUndefined(this.dataGrid.defaultIterator) && Utilities.isNullOrUndefined(this.dataGrid.iterator())) {
                const data = this.dataGrid.data();
                return data.length.toString();
            }

            if (Utilities.isNullOrUndefined(this.dataGrid.iterator())) {
                if (Utilities.isNullOrUndefinedOrBlank(this.dataGrid.staticFilter()))
                    return this.dataGrid.defaultIterator.fullExtentCount;
                return '...';
            }
            return this.dataGrid.iterator().fullExtentCount;
        });
    }

    /**
     * @implements
     */
    public refresh(): JQueryPromise<boolean> {
        if (!this.isDisposed)
            return this.viewModel?.dataGrid?.refresh();

        return Utilities.resolveImmediately(false);
    }
    
    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setFactory('AssociationGrid', AssociationGridFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');
ComponentManager.setFactory('AssoziationGrid', AssociationGridFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');